import { useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { vs, vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism"

import { useTheme } from "@/app/providers/theme-provider"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"

import { diffJson, isNoOpDiff, renderUnifiedDiff } from "./json-diff"

type RawJsonConfirmDialogProps = {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  /** Human-readable label of the resource (e.g., "explorer middleware"). */
  resourceLabel: string
  /**
   * The literal string the user must type to confirm (case-sensitive). Usually
   * the resource id or key — ensures the admin is aware of exactly what they
   * are changing.
   */
  confirmToken: string
  /** The value currently stored on the server. */
  before: unknown
  /** The value the user wants to save. */
  after: unknown
  /** Called with the reason string when the user confirms. */
  onConfirm: (reason: string) => Promise<void> | void
  /** Mutation pending state — disables buttons and shows a spinner. */
  isPending?: boolean
}

/**
 * Mandatory confirmation dialog shown before any Raw JSON save.
 *
 * Responsibilities:
 * - Shows a warning banner about the nature of Raw edits.
 * - Renders a syntax-highlighted unified diff between ``before`` and ``after``.
 * - Requires the admin to type the ``confirmToken`` verbatim.
 * - Requires a non-empty ``reason`` (min 10 chars) that will be attached to
 *   the audit entry.
 * - Refuses to confirm when the diff is a no-op (nothing actually changed).
 *
 * Safety theatre? Not quite: the combo of typed confirmation + required reason
 * is load-bearing in our audit trail story — every Raw edit gets both the
 * actor's intent and a mandatory human-readable "why" next to the before/after.
 */
export function RawJsonConfirmDialog({
  isOpen,
  onOpenChange,
  resourceLabel,
  confirmToken,
  before,
  after,
  onConfirm,
  isPending = false,
}: RawJsonConfirmDialogProps) {
  const { t } = useTranslation()
  const { resolvedTheme: theme } = useTheme()
  const [typed, setTyped] = useState("")
  const [reason, setReason] = useState("")
  const [submitError, setSubmitError] = useState<string | null>(null)

  useEffect(() => {
    if (!isOpen) {
      setTyped("")
      setReason("")
      setSubmitError(null)
    }
  }, [isOpen])

  const diff = useMemo(() => diffJson(before, after), [before, after])
  const unified = useMemo(() => renderUnifiedDiff(diff), [diff])
  const noOp = isNoOpDiff(diff)

  const reasonValid = reason.trim().length >= 10
  const tokenValid = typed === confirmToken && confirmToken.length > 0
  const canConfirm = !isPending && !noOp && reasonValid && tokenValid

  const handleConfirm = async () => {
    setSubmitError(null)
    try {
      await onConfirm(reason.trim())
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : t("adminDashboard.rawJson.errorSaveFailed", { defaultValue: "Save failed" })
      )
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {t("adminDashboard.rawJson.confirmTitle", {
              defaultValue: "Confirm raw edit to {{resourceLabel}}",
              resourceLabel,
            })}
          </DialogTitle>
          <DialogDescription>
            {t("adminDashboard.rawJson.confirmDescription", {
              defaultValue:
                "Review the diff below and confirm the change. This write is irreversible without manually rolling back.",
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 overflow-y-auto pr-1">
          <Alert variant="destructive">
            <AlertTitle>
              {t("adminDashboard.rawJson.confirmWarningTitle", {
                defaultValue: "Raw mode — no form validation",
              })}
            </AlertTitle>
            <AlertDescription>
              {t("adminDashboard.rawJson.confirmWarningDescription", {
                defaultValue:
                  "You are saving a raw JSON document. The Form tab's schema checks do not apply. If you make a mistake, the system settings cache will be updated immediately and the app may misbehave until you fix or roll back the change.",
              })}
            </AlertDescription>
          </Alert>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs tracking-wide uppercase">
                {t("adminDashboard.rawJson.diffLabel", { defaultValue: "Diff" })}
              </Label>
              <span className="text-muted-foreground text-xs">
                {t("adminDashboard.rawJson.diffStats", {
                  defaultValue: "+{{added}} −{{removed}} · {{unchanged}} unchanged",
                  added: diff.summary.added,
                  removed: diff.summary.removed,
                  unchanged: diff.summary.unchanged,
                })}
              </span>
            </div>
            {noOp ? (
              <div className="border-border text-muted-foreground rounded-md border border-dashed px-3 py-4 text-center text-sm">
                {t("adminDashboard.rawJson.noChangesDetected", {
                  defaultValue:
                    "No changes detected. Edit the JSON in the Raw tab before confirming.",
                })}
              </div>
            ) : (
              <div className="border-border max-h-80 overflow-auto rounded-md border">
                <SyntaxHighlighter
                  customStyle={{ margin: 0, fontSize: "0.75rem", background: "transparent" }}
                  language="diff"
                  style={theme === "dark" ? vscDarkPlus : vs}
                  wrapLongLines={false}
                  codeTagProps={{
                    style: { fontFamily: "ui-monospace, SFMono-Regular, monospace" },
                  }}
                >
                  {unified}
                </SyntaxHighlighter>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="raw-confirm-reason">
              {t("adminDashboard.rawJson.reasonLabel", { defaultValue: "Reason" })}{" "}
              <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="raw-confirm-reason"
              rows={3}
              value={reason}
              placeholder={t("adminDashboard.rawJson.reasonPlaceholder", {
                defaultValue:
                  "Why are you making this change? (min 10 characters, attached to the audit entry)",
              })}
              onChange={(event) => setReason(event.target.value)}
            />
            {!reasonValid && reason.length > 0 ? (
              <p className="text-destructive text-xs">
                {t("adminDashboard.rawJson.reasonTooShort", {
                  defaultValue: "Reason must be at least 10 characters.",
                })}
              </p>
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="raw-confirm-token">
              {t("adminDashboard.rawJson.typeToConfirmPrefix", { defaultValue: "Type" })}{" "}
              <code className="bg-muted rounded px-1 py-0.5 text-xs">{confirmToken}</code>{" "}
              {t("adminDashboard.rawJson.typeToConfirmSuffix", { defaultValue: "to confirm" })}
            </Label>
            <Input
              autoComplete="off"
              id="raw-confirm-token"
              spellCheck={false}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
            />
          </div>

          {submitError ? (
            <Alert variant="destructive">
              <AlertDescription>{submitError}</AlertDescription>
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            disabled={isPending}
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("adminDashboard.rawJson.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            disabled={!canConfirm}
            type="button"
            variant="destructive"
            onClick={handleConfirm}
          >
            {isPending ? (
              <>
                <Spinner />
                {t("adminDashboard.rawJson.saving", { defaultValue: "Saving…" })}
              </>
            ) : (
              t("adminDashboard.rawJson.saveRawEdit", { defaultValue: "Save raw edit" })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
